import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Router } from '@angular/router';
import { NotificationType } from 'carbon-components-angular/notification/notification-content.interface';

@Injectable({
	providedIn: 'root'
})
export class SyncService {
	constructor(
		private afStore: AngularFirestore,
		private router: Router
	) {}

	page = new BehaviorSubject<string>('login');
	theme = new BehaviorSubject<string>('white');

	userId = new BehaviorSubject<any>(null);
	userData = new BehaviorSubject<any>(null);

	fileId = new BehaviorSubject<string>('');
	fileData = new BehaviorSubject<any>(null);

	authStatus = new BehaviorSubject<string>('unauthenticated');
	email = new BehaviorSubject<string>('');
	password = new BehaviorSubject<string>('');

	workspace = new BehaviorSubject<string>('');

	currentDeal = new BehaviorSubject<string>('');

	showNotification = new BehaviorSubject<boolean>(false);
	notificationType = new BehaviorSubject<NotificationType>('success');
	notificationTitle = new BehaviorSubject<string>('');
	notificationSubtitle = new BehaviorSubject<string>('');

	notifySuccess = new BehaviorSubject<boolean>(false);
	notifyAnalyzing = new BehaviorSubject<boolean>(false);
	notifyError = new BehaviorSubject<boolean>(false);
	notifyValueNotFound = new BehaviorSubject<boolean>(false);

	dealPendingDeletion = new BehaviorSubject<string>('');
	dealDeletionModalOpen = new BehaviorSubject<boolean>(false);
	editDealModalOpen = new BehaviorSubject<boolean>(false);
	editFieldModalOpen = new BehaviorSubject<boolean>(false);

	filePendingDeletion = new BehaviorSubject<any>(null);
	fileDeletionModalOpen = new BehaviorSubject<boolean>(false);

	showSpinner = new BehaviorSubject<boolean>(false);

	page$: Observable<string> = this.page.asObservable();
	userId$: Observable<any> = this.userId.asObservable();
	userData$: Observable<any> = this.userData.asObservable();

	theme$: Observable<string> = this.theme.asObservable();

	fileData$: Observable<any> = this.fileData.asObservable();

	authStatus$: Observable<string> = this.authStatus.asObservable();
	email$: Observable<string> = this.email.asObservable();

	workspace$: Observable<string> = this.workspace.asObservable();

	currentDeal$: Observable<any> = this.currentDeal.asObservable();

	showNotification$: Observable<boolean> = this.showNotification.asObservable();
	notificationType$: Observable<NotificationType> = this.notificationType.asObservable();
	notificationTitle$: Observable<string> = this.notificationTitle.asObservable();
	notificationSubtitle$: Observable<string> = this.notificationSubtitle.asObservable();

	notifySuccess$: Observable<boolean> = this.notifySuccess.asObservable();
	notifyAnalyzing$: Observable<boolean> = this.notifyAnalyzing.asObservable();
	notifyError$: Observable<boolean> = this.notifyError.asObservable();
	notifyValueNotFound$: Observable<boolean> = this.notifyValueNotFound.asObservable();

	dealPendingDeletion$: Observable<string> = this.dealPendingDeletion.asObservable();
	dealDeletionModalOpen$: Observable<boolean> = this.dealDeletionModalOpen.asObservable();

	editDealModalOpen$: Observable<boolean> = this.editDealModalOpen.asObservable();

	filePendingDeletion$: Observable<string> = this.filePendingDeletion.asObservable();
	fileDeletionModalOpen$: Observable<boolean> = this.fileDeletionModalOpen.asObservable();

	showSpinner$: Observable<boolean> = this.showSpinner.asObservable();

	updateNotification(notificationType: NotificationType, title: string, subtitle: string): void {
		this.notificationType.next(notificationType);
		this.notificationTitle.next(title);
		this.notificationSubtitle.next(subtitle);
		this.showNotification.next(true);
		// setTimeout(() => {
		// 	this.showNotification.next(false);
		// }, 5000);
	}

	updateShowSpinner(newValue: boolean): void {
		this.showSpinner.next(newValue);
	}

	updateFilePendingDeletion(fileObject: any): void {
		this.filePendingDeletion.next(fileObject);
	}

	updateFileDeletionModalOpen(newValue: boolean): void {
		this.fileDeletionModalOpen.next(newValue);
	}

	updateDealEditModalOpen(newValue: boolean): void {
		this.editDealModalOpen.next(newValue);
	}

	updateDealPendingDeletion(newValue: string): void {
		this.dealPendingDeletion.next(newValue);
	}

	updateDealDeletionModalOpen(newValue: boolean): void {
		this.dealDeletionModalOpen.next(newValue);
	}

	updateEditFieldModalOpen(newValue: boolean): void {
		this.editFieldModalOpen.next(newValue);
	}

	updatePage(newValue: string): void {
		this.router.navigate([newValue]);
		if (newValue !== 'login') {
			this.page.next(newValue);
			if (this.authStatus.getValue() === 'authenticated') {
				this.afStore.collection('Users').doc(this.userId.getValue()).update({
					Page: ['file-data'].includes(newValue) ? 'funds' : newValue,
				});
			}
		}
	}

	updateTheme(theme: string): void {
		this.theme.next(theme);
		this.afStore.collection('Users').doc(this.userId.getValue()).update({
			Theme: theme,
		});
	}

	updateUserId(newValue: any): void {
		this.userId.next(newValue);
	}

	updateFileId(newValue: string): void {
		this.fileId.next(newValue);
		this.afStore.collection('Users').doc(this.userId.getValue()).update({
			'Current File': newValue,
		});
		this.afStore.collection('Files').doc(newValue).get().subscribe((doc) => {
			if (doc.exists) {
				const data = doc.data();
				// alert(JSON.stringify(data));
				this.updateFileData(data);
			} else {
				console.log('No such document!');
			}
		});
	}

	updateFileData(fileData: any): void {
		if (fileData) {
			console.log('fileData', fileData);
			this.afStore.collection('Files').doc(this.fileId.getValue()).set(fileData);
			this.fileData.next(fileData);
		}
	}

	updateUserData(newValue: any): void {
		if (newValue) {
			newValue['Page'] = this.getPage();
			if (newValue['Page'] === 'login') {
				newValue['Page'] = 'funds';
			}
			newValue['Theme'] = this.theme.getValue();
			this.userId$.subscribe((userId) => {
				this.afStore.collection('Users').doc(userId).set(newValue);
			});
		} else {
			newValue = {
				'Workspace': this.workspace.getValue(),
				'Theme': this.theme.getValue(),
				'Deals': [],
				'Page': 'funds',
				'Current Deal': '',
				'Current File': ''
			};
			this.afStore.collection('Users').add(newValue).then((docRef) => {
				this.updateUserId(docRef.id);
				console.log('docRef.id', docRef.id);
			});
		}
		this.userData.next(newValue);
	}

	setUserDataTimestamp() {
		const userID = this.userId.getValue();
		const currentTime = new Date().getTime().toString();
		this.afStore.collection('Users').doc(userID).update({
			'Timestamp': currentTime
		}).then();
		return currentTime;
	}

	getCurrentUserData() {
		const userID = this.userId.getValue();
		this.afStore.collection('Users').doc(userID).get().subscribe((doc) => {
			return doc.data();
		});
		return null
	}

	updateUserDataUsingFunction(callback: any) {
		this.showSpinner.next(true);
		const userID = this.userId.getValue();
		for (let i = 0; i < 10; i++) {
			const beforeUserData = this.setUserDataTimestamp();
			if (beforeUserData) {
				try {
					// @ts-ignore
					const beforeTimestamp = beforeUserData['Timestamp'];
					const afterUserData = callback(beforeTimestamp);
					const checkUserData = this.getCurrentUserData();
					if (checkUserData) {
						const checkTimestamp = checkUserData['Timestamp'];
						if (checkTimestamp === beforeTimestamp) {
							// set userdata to afterUserData
							this.afStore.collection('Users').doc(userID).set(afterUserData);
							this.showSpinner.next(false);
							return;
						} else {
							this.userData.next(checkUserData);
						}
					}
				} catch (error) {
					console.log(error);
				}
			}
			setTimeout(() => {}, Math.random() * 1000);
		}
		this.showSpinner.next(false);
	}

	updateAuthStatus(newValue: string): void {
		this.authStatus.next(newValue);
	}

	updateWorkspace(newValue: string): void {
		this.workspace.next(newValue);
	}

	updateEmail(newValue: string): void {
		this.email.next(newValue);
	}

	updatePassword(newValue: string): void {
		this.password.next(newValue);
	}

	updateCurrentDeal(newValue: string): void {
		this.currentDeal.next(newValue);
		this.afStore.collection('Users').doc(this.userId.getValue()).update({
			'Current Deal': newValue
		});
	}

	updateShowNotification(newValue: boolean): void {
		this.showNotification.next(newValue);
	}

	updateNotificationType(newValue: NotificationType): void {
		this.notificationType.next(newValue);
	}

	updateNotificationTitle(newValue: string): void {
		this.notificationTitle.next(newValue);
	}

	updateNotificationSubtitle(newValue: string): void {
		this.notificationSubtitle.next(newValue);
	}

	updateNotifySuccess(newValue: boolean): void {
		this.notifySuccess.next(newValue);
	}

	updateNotifyAnalyzing(newValue: boolean): void {
		this.notifyAnalyzing.next(newValue);
	}

	updateNotifyError(newValue: boolean): void {
		this.notifyError.next(newValue);
	};

	updateNotifyValueNotFound(newValue: boolean): void {
		this.notifyValueNotFound.next(newValue);
	};

	getPage() {
		return this.page.getValue();
	}
}
